import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { readerLogin, readerRegister, readerReset, forgotPassword, googleSignIn, errorDetail } from "@/lib/reader";
import RecoveryCodeDialog from "@/components/reader/RecoveryCodeDialog";

const inputCls =
  "w-full bg-[#0B0C10] border border-white/15 px-4 py-3 text-text-primary placeholder:text-white/30 focus:outline-none focus:border-primary-gold transition-colors";

function GoogleButton() {
  return (
    <button
      type="button"
      onClick={googleSignIn}
      data-testid="reader-google-button"
      className="w-full flex items-center justify-center gap-3 bg-white text-[#1f1f1f] font-medium py-3 hover:bg-white/90 transition-colors"
    >
      <svg viewBox="0 0 48 48" className="w-5 h-5" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
      </svg>
      Continue with Google
    </button>
  );
}

const SUBMIT_LABEL = { register: "Create account", login: "Sign in", reset: "Set new password", forgot: "Email me a reset link" };

function ForgotPanel({ email, setEmail, onCode, onBack }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      toast.error(errorDetail(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="reader-forgot-panel">
      <h3 className="font-serif-display text-2xl">Forgot your password?</h3>
      {sent ? (
        <p className="text-sm mt-3 leading-relaxed border border-primary-gold/30 bg-primary-gold/5 px-4 py-3" data-testid="reader-forgot-sent">
          If an account exists for <strong>{email}</strong>, a reset link is on its way. It works once and expires in 60 minutes — check your spam folder too.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-4 mt-4">
          <p className="text-soft text-sm leading-relaxed">Enter your email and we'll send you a link to choose a new password.</p>
          <input required type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="reader-forgot-email-input" />
          <button type="submit" disabled={busy} data-testid="reader-forgot-submit" className="w-full flex items-center justify-center gap-2 bg-primary-gold text-[#0B0C10] font-mono-ui text-xs uppercase tracking-[0.2em] py-4 hover:opacity-90 disabled:opacity-50">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {SUBMIT_LABEL.forgot}
          </button>
        </form>
      )}
      <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5 text-xs">
        <button onClick={onCode} data-testid="reader-use-recovery-code" className="text-soft hover:text-primary-gold transition-colors">Use a recovery code instead</button>
        <button onClick={onBack} data-testid="reader-forgot-back" className="text-soft hover:text-primary-gold transition-colors">Back to sign in</button>
      </div>
    </div>
  );
}

export default function AuthPanel({ onSignedIn, initialMode = "register" }) {
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: "", email: "", password: "", code: "" });
  const [busy, setBusy] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") {
        const res = await readerRegister({ name: form.name, email: form.email, password: form.password });
        toast.success("Account created — welcome!");
        setRecoveryCode(res.recovery_code);
      } else if (mode === "reset") {
        const res = await readerReset({ email: form.email, recovery_code: form.code, new_password: form.password });
        toast.success("Password changed — you're signed in.");
        setRecoveryCode(res.recovery_code);
      } else {
        await readerLogin({ email: form.email, password: form.password });
        toast.success("Welcome back!");
        onSignedIn();
      }
    } catch (err) {
      toast.error(errorDetail(err));
    } finally {
      setBusy(false);
    }
  };

  const needsStrongPassword = mode !== "login";

  if (mode === "forgot") {
    return (
      <div className="border border-white/10 bg-[#121212]/90 backdrop-blur p-8 sm:p-10" data-testid="reader-auth-panel">
        <ForgotPanel
          email={form.email}
          setEmail={(v) => setForm((f) => ({ ...f, email: v }))}
          onCode={() => setMode("reset")}
          onBack={() => setMode("login")}
        />
      </div>
    );
  }

  return (
    <div className="border border-white/10 bg-[#121212]/90 backdrop-blur p-8 sm:p-10" data-testid="reader-auth-panel">
      {recoveryCode && <RecoveryCodeDialog code={recoveryCode} onDone={onSignedIn} />}
      <div className="flex gap-6 border-b border-white/10 mb-8">
        {[
          ["register", "Create account"],
          ["login", "Sign in"],
        ].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setMode(id)}
            data-testid={`reader-auth-tab-${id}`}
            className={`pb-3 -mb-px border-b-2 font-mono-ui text-xs uppercase tracking-[0.18em] transition-colors ${
              mode === id ? "border-primary-gold text-primary-gold" : "border-transparent text-soft hover:text-text-primary"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === "reset" ? (
        <div className="mb-6" data-testid="reader-reset-intro">
          <h3 className="font-serif-display text-2xl">Reset your password</h3>
          <p className="text-soft text-sm mt-2 leading-relaxed">
            Enter your email and the recovery code you saved when you created your account.
          </p>
        </div>
      ) : (
        <>
          <GoogleButton />
          <div className="flex items-center gap-4 my-6 text-soft font-mono-ui text-[10px] uppercase tracking-[0.25em]">
            <span className="h-px flex-1 bg-white/10" /> or with email <span className="h-px flex-1 bg-white/10" />
          </div>
        </>
      )}

      <form onSubmit={submit} className="space-y-4">
        {mode === "register" && (
          <input required placeholder="Your name" value={form.name} onChange={set("name")} className={inputCls} data-testid="reader-name-input" />
        )}
        <input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={inputCls} data-testid="reader-email-input" />
        {mode === "reset" && (
          <input
            required
            placeholder="Recovery code (e.g. ABCD-EFGH-JKLM)"
            value={form.code}
            onChange={set("code")}
            className={`${inputCls} font-mono-ui uppercase tracking-[0.15em]`}
            data-testid="reader-recovery-code-input"
          />
        )}
        <input
          required
          type="password"
          minLength={needsStrongPassword ? 8 : undefined}
          placeholder={mode === "reset" ? "New password (8+ characters)" : needsStrongPassword ? "Password (8+ characters)" : "Password"}
          value={form.password}
          onChange={set("password")}
          className={inputCls}
          data-testid="reader-password-input"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="reader-auth-submit"
          className="w-full flex items-center justify-center gap-2 bg-primary-gold text-[#0B0C10] font-mono-ui text-xs uppercase tracking-[0.2em] py-4 hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {SUBMIT_LABEL[mode]}
        </button>
      </form>

      {mode === "login" && (
        <button onClick={() => setMode("forgot")} data-testid="reader-forgot-password" className="mt-5 text-xs text-soft hover:text-primary-gold transition-colors">
          Forgot your password?
        </button>
      )}
      {mode === "reset" && (
        <button onClick={() => setMode("login")} data-testid="reader-back-to-login" className="mt-5 text-xs text-soft hover:text-primary-gold transition-colors">
          Back to sign in
        </button>
      )}
    </div>
  );
}
